import type { CareFormat } from "@src/constants/filter-constants";
import type { ProviderSlot } from "@src/interfaces/provider.interface";

export type { ProviderSlot } from "@src/interfaces/provider.interface";

import { callApi, extractApiErrorMessage, hasSuccessFlag } from "@src/utils/api-client";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import { validateSchedulingRequestParams } from "@src/utils/scheduling-metadata";
import ClientLogsService from "./client-logs.service";
import { buildProviderSlotsConfig } from "./configs";

/**
 * Provider Slots Service
 *
 * Service layer for fetching provider availability slots from Provider Hub API.
 * This service interfaces with the internal provider-slots API route using POST method.
 */

/**
 * Request interface for provider slots service
 */
export interface ProviderSlotsRequest {
  userUuid: string;
  careFormat: CareFormat;
  practiceId: number;
  providerId: number;
  departmentIds: number[]; // Array of department IDs
  startDate: string; // MM/DD/YYYY format
  endDate: string; // MM/DD/YYYY format
  userLatitude: number;
  userLongitude: number;
  minimum_day_time?: string; // Optional minimum datetime with timezone offset
  duration?: number; // Optional duration in minutes
  clinicianType?: string; // Optional clinician type
}

/**
 * Response interface for provider slots service
 */
export interface ProviderSlotsResponse {
  success: boolean;
  data?: {
    slots: ProviderSlot[];
    transactionId: string;
  };
  error?: string;
}

interface BackendProviderSlotsResponse {
  transactionId?: string;
  message?: ProviderSlot[] | string;
  error?: string;
  /** True when `message` is stale fallback-cache data served after an upstream failure. */
  degraded?: boolean;
}

/**
 * Fetches provider availability slots based on the provided parameters
 *
 * @param request - The provider slots request parameters
 * @returns Promise with provider slots response
 *
 * @example
 * ```typescript
 * const response = await getProviderAvailableSlots({
 *   userUuid: "user-123-uuid",
 *   careFormat: "in-person",
 *   practiceId: 123,
 *   providerId: 456,
 *   departmentIds: [789, 101],
 *   startDate: "11/01/2025",
 *   endDate: "11/07/2025",
 *   userLatitude: 40.7128,
 *   userLongitude: -74.0060
 * });
 *
 * if (response.success) {
 *   logger.info({ message: "Available slots:", slots: response.data?.slots });
 *   logger.info({ message: "Transaction ID:", transactionId: response.data?.transactionId });
 * } else {
 *   logger.error({ message: "Error:", error: response.error });
 * }
 * ```
 */
export async function getProviderAvailableSlots(
  request: ProviderSlotsRequest,
): Promise<ProviderSlotsResponse> {
  try {
    const validationErrors = validateSchedulingRequestParams({
      clinicianType: request.clinicianType,
      duration: request.duration,
    });

    if (validationErrors.length > 0) {
      return {
        success: false,
        error: validationErrors.join(", "),
      };
    }

    // Prepare request body for POST method
    // Call the internal API route with POST method
    const { data: payload, response } = await callApi(buildProviderSlotsConfig(request));
    const isOk = response.ok;

    if (!isOk) {
      const responseError = extractApiErrorMessage(payload, response);
      ClientLogsService.logError(
        request.userUuid || "unknown",
        "provider-slots.service.getProviderAvailableSlots",
        "Provider slots request failed",
        new Error(responseError),
        {
          errorType: getFailureTypeForHttpStatus(response.status),
          dependencyName: "ProviderSlotsAPI",
          functionName: "getProviderAvailableSlots",
        },
      );
      return {
        success: false,
        error: responseError,
      };
    }

    if (hasSuccessFlag<ProviderSlotsResponse>(payload)) {
      return payload;
    }

    if (payload === null) {
      ClientLogsService.logError(
        request.userUuid || "unknown",
        "provider-slots.service.getProviderAvailableSlots",
        "Provider slots service error",
        new Error("Invalid JSON response"),
        {
          errorType: "DependencyFailure",
          dependencyName: "ProviderSlotsAPI",
          functionName: "getProviderAvailableSlots",
        },
      );
      return { success: false, error: "Invalid JSON response" };
    }

    const backendPayload = payload as BackendProviderSlotsResponse;

    if (typeof backendPayload.message === "string" && backendPayload.message.trim()) {
      return {
        success: false,
        error: backendPayload.message,
      };
    }

    if (typeof backendPayload.error === "string" && backendPayload.error.trim()) {
      return {
        success: false,
        error: backendPayload.error,
      };
    }

    if (backendPayload.degraded) {
      // Telemetry only (no UX change): tracks how often browsing data is stale
      // fallback-cache data rather than live, to inform whether a visible
      // degraded-mode indicator is worth adding later.
      ClientLogsService.logWebAction(
        request.userUuid || "unknown",
        "provider-slots.service.getProviderAvailableSlots",
        "degraded_availability_response",
        { transactionId: backendPayload.transactionId || "" },
      );
    }

    return {
      success: true,
      data: {
        slots: Array.isArray(backendPayload.message) ? backendPayload.message : [],
        transactionId: backendPayload.transactionId || "",
      },
    };
  } catch (error) {
    // Handle network or unexpected errors
    ClientLogsService.logError(
      request.userUuid || "unknown",
      "provider-slots.service.getProviderAvailableSlots",
      `Provider slots service error`,
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "ProviderSlotsAPI",
        functionName: "getProviderAvailableSlots",
      },
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}
